/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';
import { dateToEpoch, epochToDate } from '../UtilityFunctions';
import { CONTACT_RESULT_CODES, OUTBOUND_CALL_CODES } from '../../contracts/constants';
import dayjs from 'dayjs';

const useComments = () => {
  const { token } = useAuthContext();
  const [searchParams] = useSearchParams(); // To retrieve query parameters
  const { loanNumber } = useLoanContext();
  const location = useLocation();
  const { pathname } = location;
  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const rows = parseInt(searchParams.get('rows') || '10', 10);
  const loanNumberRegex = /^\d{12}$/;
  const navigate = useNavigate();
  const { get } = useAbortController();
  const [isCommenting, setIsCommenting] = useState(false);
  const [updatingIndex, setUpdatingIndex] = useState(-1);
  const [updateCount, setUpdateCount] = useState(0);
  const [ascendSort, setAscendSort] = useState(false);

  // Fetch communication data using react-query
  const { data, isLoading } = useQuery({
    queryKey: ['comments', loanNumber, category, search, updateCount, page, rows, token, ascendSort],
    queryFn: async () => {
      try {
        if (!token || !loanNumber) return { data: [] };
        const req = {
          url: `live/loan/${loanNumber}/event/SCL`,
          method: 'POST',
          data: { active: true },
        };
        const headers = {
          'Session-Token': token,
        };
        const res = await axiosCall(req, undefined, headers);
        if (!res) throw new Error('SCL Servicing Loan event comments are inactive');
        const offset = (page - 1) * rows;
        const request = {
          url: `live/loan/${loanNumber}/event/SCL/comment/list`,
          method: 'GET',
        };
        const response = await axiosCall(request, undefined, headers);

        const sortedresponse = response.sort((a, b) => {
          const aTimestamp = a.originalCreateDateTime ? a.originalCreateDateTime : a.creationDate;
          const bTimestamp = b.originalCreateDateTime ? b.originalCreateDateTime : b.creationDate;
          return aTimestamp - bTimestamp;
        });

        // Filter comments based on category and search criteria
        if (!ascendSort) sortedresponse.reverse();
        const filteredComments = sortedresponse.filter((comment) => {
          const matchesCategory = category ? category.split(',').find((c) => c === comment.commentCategory) : true; // Include all categories if none is specified

          const matchesSearch = search ? comment.text.toLowerCase().includes(search.toLowerCase()) : true; // Include all if no search term is provided

          return comment.active && matchesCategory && matchesSearch;
        });

        // Paginate the filtered comments
        const paginatedData = filteredComments.slice(offset, offset + rows);

        return { data: paginatedData, total: filteredComments.length, commentList: sortedresponse };
      } catch (error) {
        console.error(`useComments.caught: ${error.message}`);
        // Simulated fetch call. Replace this with actual API call.
        return {
          data: [],
          total: 0,
          commentList: [],
        };
      }
    },
  });

  const sortAscending = useCallback((doAscendSort: boolean) => {
    setAscendSort(doAscendSort);
    reloadComments();
  }, []);

  const comments = useMemo(() => (!isLoading && data && Array.isArray(data?.data) ? data.data : []), [isLoading, data]);
  const totalComments = useMemo(() => (!isLoading && data && data.total ? data.total : 0), [isLoading, data]);
  const commentList = useMemo(
    () => (!isLoading && data && Array.isArray(data?.commentList) ? data.commentList : []),
    [isLoading, data],
  );
  const managerComments = useMemo(() => {
    const managerCommentCategories = ['1', '2', '3', '4', '5'];

    return managerCommentCategories
      .map((category) => {
        // filter for comments in the given category
        const categoryComments = commentList.filter(
          (comment) => comment.active && comment.commentCategory === category,
        );

        // check if any comments exist in the given category, if not return null for the category
        if (categoryComments.length > 0) {
          // find the most recent comment in the given category
          const mostRecentComment = categoryComments.reduce((mostRecent, currentComment) => {
            const curCreateDate = currentComment.originalCreateDateTime
              ? currentComment.originalCreateDateTime
              : currentComment.creationDate;
            const mostRecentCreateDate = mostRecent.originalCreateDateTime
              ? mostRecent.originalCreateDateTime
              : mostRecent.creationDate;
            return curCreateDate > mostRecentCreateDate ? currentComment : mostRecent;
          });

          // if the comment text is *, the comment should not be shown
          if (mostRecentComment.text === '*') {
            return null;
          }

          return mostRecentComment;
        }

        return null;
      })
      .filter((comment) => comment !== null);
  }, [commentList]);
  const numOutboundCalls = useMemo(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 6);
    return commentList.filter((c) => {
      const createDate = c.originalCreateDateTime ? c.originalCreateDateTime : c.creationDate;
      return c.active && epochToDate(createDate) >= sevenDaysAgo && OUTBOUND_CALL_CODES.includes(c.actionCode);
    }).length;
  }, [commentList]);
  const numContactMade = useMemo(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 6);
    return commentList.filter((c) => {
      const createDate = c.originalCreateDateTime ? c.originalCreateDateTime : c.creationDate;
      return (
        c.active &&
        epochToDate(createDate) >= sevenDaysAgo &&
        OUTBOUND_CALL_CODES.includes(c.actionCode) &&
        CONTACT_RESULT_CODES.includes(c.resultCode)
      );
    }).length;
  }, [commentList]);

  const reloadComments = useCallback(() => setUpdateCount((prev) => prev + 1), []);

  // Handle form validation and submission
  const createComment = useCallback(
    async (e, formData, actionCodes, categories, resultCodes, setErrorData, setFormData, clearForm) => {
      const data = { ...formData, originalCreateDateTime: dayjs().unix() };
      const errorData = {
        actionCode: '',
        actionCodeDescription: '',
        actionCodeKeyword: '',
        codes: '',
        commentCategory: '',
        commentCategoryDescription: '',
        commentCategoryKeyword: '',
        loanNumbers: '',
        loanNumber: '',
        text: '',
        promiseToPayDate: '',
        promiseToPayAmt: '',
        resultCode: '',
        resultCodeDescription: '',
        resultCodeKeyword: '',
        generic: '',
      };
      try {
        e.preventDefault();
        setIsCommenting(true);
        // business exports want users to have control over text formatting.
        // data.text = data.codes + data.text;
        delete data.codes;
        delete data.loanNumber;
        if (!data) {
          errorData.generic = 'Could not add your comment at this time';
          setErrorData(errorData);
          return;
        }
        if (!data.loanNumbers) {
          errorData.loanNumber = 'Required';
        }
        if (!data.actionCode) {
          errorData.actionCode = 'Required';
        }
        if (!data.resultCode) {
          errorData.resultCode = 'Required';
        }
        if (!data.text) {
          errorData.text = 'Required';
        }
        if (!data.commentCategory) {
          errorData.commentCategory = 'Required';
        }
        if (data.promiseToPayDate || data.promiseToPayAmt) {
          const todayObj = new Date();
          const inputDateObj = new Date(data.promiseToPayDate);
          todayObj.setDate(todayObj.getDate() - 1); // Subtract 1 day
          todayObj.setHours(0, 0, 0, 0);
          const today = todayObj.getTime();
          const inputDate = inputDateObj.getTime();

          if (isNaN(inputDateObj.getTime())) {
            errorData.promiseToPayDate = 'Invalid date';
          } else if (today > inputDate) {
            errorData.promiseToPayDate = 'Date must be in the future';
          }
          if (data.promiseToPayAmt < 0) {
            errorData.promiseToPayAmt = 'Amount must be a positive value';
          }

          if (!errorData.promiseToPayDate && !errorData.promiseToPayAmt) {
            inputDateObj.setDate(inputDateObj.getDate() + 1); // Add 1 day to ensure correct date is shown in comment
            data['text'] =
              (data['text'] ? data['text'] + '\n' : '') +
              'Promise to pay $' +
              data.promiseToPayAmt +
              ' on ' +
              inputDateObj.toDateString();

            // convert promise to pay date to timestamp
            data['promiseToPayDate'] = dateToEpoch(inputDateObj);
          }
        }
        const actionCodeExists = actionCodes.find((ac) => ac.code === formData.actionCode);
        if (!actionCodeExists) {
          errorData.actionCode = 'Please select an option';
        }
        const resultCodeExists = resultCodes.find((rc) => rc.code === formData.resultCode);
        if (!resultCodeExists) {
          errorData.resultCode = 'Please select an option';
        }
        const categoryExists = categories.find((c) => c.code === formData.commentCategory);
        if (!categoryExists) {
          errorData.commentCategory = 'Please select an option';
        }
        setErrorData(errorData);
        if (Object.values(errorData).filter((v) => v).length > 0) return;

        //Convert comma-delimited strings to newline-delimited strings of loan numbers for standardization
        const loanNumbers = data.loanNumbers.replace(/,/g, '\n');
        //Split into Set to rid the array of all duplicate loan numbers and empty/null strings
        const batchLoans = [...new Set<string>(loanNumbers.split('\n').filter(Boolean))];

        let validLoans = 0;
        let invalidLoans = 0;

        for await (const loan of batchLoans) {
          if (loanNumberRegex.test(loan)) {
            validLoans++;
            const request = {
              url: `live/loan/${loan}/event/SCL/comment/add`,
              method: 'POST',
              data: { ...data },
            };
            const config = get();
            const headers = {
              'Session-Token': token,
            };
            const response = await axiosCall(request, config, headers);
            console.log('useComments.createComment.response', response);
          } else {
            invalidLoans++;
          }
        }

        setUpdateCount((prev) => prev + 1);
        setErrorData(errorData);
        if (clearForm) {
          setFormData((prev) => ({
            ...prev,
            actionCode: '',
            actionCodeDescription: '',
            actionCodeKeyword: '',
            codes: '',
            commentCategory: '',
            commentCategoryDescription: '',
            commentCategoryKeyword: '',
            loanNumbers: `${loanNumber}`,
            loanNumber: '',
            text: '',
            promiseToPayDate: ``,
            promiseToPayAmt: '',
            resultCode: '',
            resultCodeDescription: '',
            resultCodeKeyword: '',
            success: 'Your comment was added successfully!',
            validLoans: validLoans,
            invalidLoans: invalidLoans,
          }));
        }
        reloadComments();
      } catch (error) {
        console.error(`useComments.createComment.caught: ${error.message}`);
        errorData.generic = 'Your comment could not be added at this time';
        setErrorData(errorData);
      } finally {
        setIsCommenting(false);
      }
    },
    [get, loanNumber, reloadComments, token],
  );

  // Handle submission of edited note
  const updateComment = useCallback(
    async (index, value) => {
      try {
        setUpdatingIndex(index);
        const comment = comments[index];
        const request = {
          url: `live/comment/${comment.commentNumber}/sequence/${comment.sequenceNumber}`,
          method: 'POST',
          data: { text: value },
        };
        const config = get();
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useComments.updateComment.response`, response);
        setUpdateCount((prev) => prev + 1); // Trigger re-fetch
      } catch (error) {
        console.error(`useComments.updateComment.caught: ${error.message}`);
      } finally {
        setUpdatingIndex(-1);
      }
    },
    [comments, get, token, searchParams, pathname, navigate],
  );

  // Handle removal of a comment
  const deleteComment = useCallback(
    async (index) => {
      try {
        setUpdatingIndex(index);
        const comment = comments[index];
        const request = {
          url: `live/comment/${comment.commentNumber}/sequence/${comment.sequenceNumber}`,
          method: 'POST',
          data: { active: false },
        };
        const config = get();
        const headers = {
          'Session-Token': token,
        };
        await axiosCall(request, config, headers);
        setUpdateCount((prev) => prev + 1); // Trigger re-fetch
      } catch (error) {
        console.error(`useComments.deleteComment.caught: ${error.message}`);
      } finally {
        setUpdatingIndex(-1);
      }
    },
    [comments, get, token],
  );

  return {
    comments,
    sortAscending,
    commentList,
    numOutboundCalls,
    numContactMade,
    totalComments,
    managerComments,
    reloadComments,
    isCommenting,
    loadingComments: isLoading,
    updatingComment: updatingIndex,
    createComment,
    updateComment,
    deleteComment,
  };
};

export default useComments;
